/**
 * i18n — lightweight string lookup + interpolation.
 *
 * Two entry points:
 *   - `useTranslation()` — hook for components. Reads current language from
 *     IntakeContext. Returns { t, lang }.
 *   - `tForLang(lang, key, vars)` — pure function for rendering the welcome
 *     screen (which shows all six languages at once before the user picks).
 *
 * Also provides `useLocalized(localizedString)` for pulling the active
 * language out of config-driven LocalizedString objects (field labels,
 * options, glossary definitions). Falls back to English when a language
 * string is absent from the LocalizedString object.
 */

import { useMemo } from 'react';
import type { Language } from '../api/types';
import { useIntake } from '../state/IntakeContext';
import en from './strings.en.json';
import es from './strings.es.json';
import ptBR from './strings.pt-BR.json';
import ko from './strings.ko.json';
import vi from './strings.vi.json';
import gn from './strings.gn.json';

// Shape is identical across files (English is the authoritative key set).
export type StringKey = keyof typeof en;

const BUNDLES: Record<Language, Record<string, string>> = {
  en,
  es,
  'pt-BR': ptBR,
  ko,
  vi,
  gn,
};

export const DEFAULT_LANGUAGE: Language = 'en';

/**
 * LocalizedString shape from the program config.
 * `ko`, `vi`, and `gn` are optional so test fixtures and legacy code
 * don't need them — `localizedFor` falls back to English when absent.
 */
export interface LocalizedString {
  en: string;
  es: string;
  'pt-BR': string;
  ko?: string;
  vi?: string;
  gn?: string;
}

// ---------------------------------------------------------------------------
// Pure functions
// ---------------------------------------------------------------------------

/** Interpolate `{varname}` placeholders. Missing vars render as the literal
 *  `{varname}` so missing data is visible rather than silent. */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    key in vars ? String(vars[key]) : match,
  );
}

/** Look up a key in the given language. Falls back to English if the key is
 *  missing from the bundle (shouldn't happen with consistent files, but
 *  defend against accidental key removal during translation passes). */
export function tForLang(
  lang: Language,
  key: StringKey,
  vars?: Record<string, string | number>,
): string {
  const bundle = BUNDLES[lang];
  const template = bundle[key] ?? BUNDLES[DEFAULT_LANGUAGE][key] ?? key;
  return interpolate(template, vars);
}

/** Pick the current-language string out of a config-driven LocalizedString.
 *  Falls back to English when the language key is absent (e.g. older config
 *  objects that predate a language addition). */
export function localizedFor(lang: Language, ls: LocalizedString): string {
  return ls[lang as keyof LocalizedString] ?? ls.en;
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface UseTranslation {
  /** Current active language. Defaults to English before detect_language runs. */
  lang: Language;
  /** Lookup + interpolate. */
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
  /** Pick the active-language string from a LocalizedString object. */
  localized: (ls: LocalizedString) => string;
}

export function useTranslation(): UseTranslation {
  const { state } = useIntake();
  const lang = state.language ?? DEFAULT_LANGUAGE;

  return useMemo<UseTranslation>(
    () => ({
      lang,
      t: (key, vars) => tForLang(lang, key, vars),
      localized: (ls) => localizedFor(lang, ls),
    }),
    [lang],
  );
}
